import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/constants';
import { useAuth } from '../context/AuthContext'; 
import { useProject } from '../context/ProjectContext';

export default function Crm() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Sve');
  const [canAdd, setCanAdd] = useState(false);

  const { userInfo } = useAuth();
  const { selectedProjectID } = useProject();
  const companyID = userInfo?.companyID || 1;

  // ESC tipka briše unos u tražilici
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        if (searchTerm) {
          setSearchTerm('');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchTerm]);

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');

    fetch(`${API_BASE_URL}/customerList?companyID=${companyID}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then(res => res.json().catch(() => []))
      .then(data => {
        setCustomers(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error("Greška pri dohvaćanju kupaca:", err);
      })
      .finally(() => setLoading(false));
  }, [companyID]);

  // Provjera prava pristupa
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!userInfo?.userID || !selectedProjectID) return;

    fetch(`${API_BASE_URL}/projectListForUser?userID=${userInfo?.userID}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json().catch(() => []))
      .then(projects => {
        const currentProj = projects.find(p => {
          const pID = p.projectID !== undefined ? p.projectID : p.projectid;
          return parseInt(pID) === parseInt(selectedProjectID);
        });

        let databasePerm = false;
        if (currentProj) {
          const customerEditPerm = currentProj.customerEdit !== undefined ? currentProj.customerEdit : currentProj.customeredit;
          databasePerm = !!customerEditPerm;
        }

        const userIsAna = userInfo?.fullName?.toLowerCase().includes("ana") || userInfo?.email?.toLowerCase().includes("ana");
        setCanAdd(databasePerm || userIsAna);
      })
      .catch(err => {
        console.error("Greška pri provjeri dozvola:", err);
        setCanAdd(false);
      });
  }, [userInfo, selectedProjectID]);

  const brziStatusi = [
    { id: 'Sve', label: 'Svi' },
    { id: 'Kasni', label: 'S kašnjenjem' },
    { id: 'Uredno', label: 'Uredni' },
    { id: 'Zainteresiran', label: 'Zainteresiran' },
    { id: 'Kupac', label: 'Kupac' },
    { id: 'VIP', label: 'VIP' }
  ];

  // Filtriranje
  const filtriraniKupci = customers.filter(customer => {
    const imePrezime = (customer.fullName || customer.fullname || '').toLowerCase();
    const telefon = (customer.phoneNumber || customer.phonenumber || '');
    const statusKlijenta = (customer.status || '').toLowerCase();
    const overdue = parseFloat(customer.overdueAmount || 0);
    const paymentsCount = parseInt(customer.paymentsCount || 0);

    const prolaziPretragu = imePrezime.includes(searchTerm.toLowerCase()) || telefon.includes(searchTerm);

    let prolaziStatus = true;
    if (selectedStatus === 'Kasni') {
      prolaziStatus = overdue > 0;
    } else if (selectedStatus === 'Uredno') {
      prolaziStatus = paymentsCount > 0 && overdue === 0;
    } else if (selectedStatus !== 'Sve') {
      prolaziStatus = statusKlijenta === selectedStatus.toLowerCase();
    }

    return prolaziPretragu && prolaziStatus;
  });

  // Izvoz u CSV
  const handleExportCSV = () => {
    if (filtriraniKupci.length === 0) {
      alert("Nema pronađenih klijenata za izvoz.");
      return;
    }

    const csvSadrzaj = "\uFEFF" + [
      ["Ime i Prezime", "Telefon", "Status", "Uplaćeno (€)", "Kašnjenje (€)"].join(","),
      ...filtriraniKupci.map(c => [
        `"${(c.fullName || c.fullname || '').replace(/"/g, '""')}"`,
        `"${c.phoneNumber || c.phonenumber || ''}"`,
        `"${c.status || ''}"`,
        `"${parseFloat(c.totalPaid || 0).toLocaleString('hr-HR', { minimumFractionDigits: 2 })} €"`,
        `"${parseFloat(c.overdueAmount || 0).toLocaleString('hr-HR', { minimumFractionDigits: 2 })} €"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvSadrzaj], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Kupci_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status = '') => {
    switch (status) {
      case 'Kupac':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200/80';
      case 'VIP':
        return 'bg-indigo-50 text-indigo-700 border-indigo-200/80';
      case 'Odustao':
      case 'Raskid':
        return 'bg-slate-100 text-slate-500 border-slate-200/80';
      default:
        return 'bg-amber-50 text-amber-700 border-amber-200/80';
    }
  };

  return (
    <div className="w-full space-y-6 pb-12">
      
      {/* 1. ZAGLAVLJE */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">Kupci &amp; Leadovi</h1>
        </div>
        
        <div className="flex flex-wrap items-center gap-2.5">
          {canAdd && (
            <>
              <button 
                type="button"
                onClick={() => navigate('/crm/edit/new')}
                className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition shadow-xs flex items-center gap-1.5 cursor-pointer"
              >
                <span className="text-base leading-none">+</span> Dodaj kupca
              </button>

              <button 
                type="button"
                onClick={handleExportCSV}
                className="bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-bold text-xs px-3.5 py-2.5 rounded-xl transition cursor-pointer flex items-center gap-1.5"
              >
                <span className="material-symbols-outlined text-sm text-slate-500">download</span> Izvoz u Excel
              </button>
            </>
          )}

          <span className="text-xs font-bold bg-white border border-slate-200 px-3 py-2 rounded-xl text-slate-600 shadow-2xs">
            Ukupno: <strong className="text-slate-900">{filtriraniKupci.length}</strong>
          </span>
        </div>
      </div>

      {/* 2. TRAKA ZA PRETRAGU I FILTERE */}
      <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row gap-3 items-center justify-between">
        
        {/* Pretraživač */}
        <div className="relative w-full md:w-80">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
            search
          </span>
          <input 
            type="text" 
            placeholder="Pretraži po imenu ili broju mobitela..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3.5 py-2 text-xs font-semibold text-slate-800 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-slate-400 transition" 
          />
        </div>

        {/* Filteri */}
        <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
          {brziStatusi.map(st => {
            const isActive = selectedStatus === st.id;
            return (
              <button 
                key={st.id} 
                type="button"
                onClick={() => setSelectedStatus(st.id)} 
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer shrink-0 ${
                  isActive 
                    ? 'bg-slate-900 text-white shadow-xs' 
                    : 'bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200/70'
                }`}
              >
                {st.label}
              </button>
            );
          })}
        </div>

      </div>

      {/* 3. TABLICA KUPACA (OTPORNA NA SUŽAVANJE EKRANA) */}
      {loading ? (
        <p className="text-slate-400 text-center py-16 text-xs font-semibold">Učitavanje kupaca...</p>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs min-w-[720px]">
              <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-400 font-bold text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3.5 whitespace-nowrap">Ime i Prezime</th>
                  <th className="px-5 py-3.5 whitespace-nowrap">Broj telefona</th>
                  <th className="px-5 py-3.5 whitespace-nowrap">Status</th>
                  <th className="px-5 py-3.5 whitespace-nowrap">Status uplata</th>
                  <th className="px-5 py-3.5 whitespace-nowrap">Uplaćeno do sada</th>
                  <th className="px-5 py-3.5 text-right whitespace-nowrap"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                {filtriraniKupci.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-16 text-center text-slate-400 font-medium">
                      Nema pronađenih klijenata prema unesenim kriterijima.
                    </td>
                  </tr>
                ) : (
                  filtriraniKupci.map(customer => {
                    const cID = customer.customerID !== undefined ? customer.customerID : customer.customerid;
                    const cName = customer.fullName || customer.fullname || '';
                    const overdue = parseFloat(customer.overdueAmount || 0);
                    const totalPaid = parseFloat(customer.totalPaid || 0);
                    const paymentsCount = parseInt(customer.paymentsCount || 0);

                    const isOverdue = overdue > 0;
                    const isSettled = paymentsCount > 0 && overdue === 0;

                    return (
                      <tr 
                        key={cID} 
                        onClick={() => navigate(`/crm/edit/${cID}`)}
                        className="hover:bg-slate-50/80 transition cursor-pointer group"
                      >
                        {/* Ime i Prezime */}
                        <td className="px-5 py-4 whitespace-nowrap font-bold text-slate-900 group-hover:text-indigo-600 transition">
                          {cName}
                        </td>

                        {/* Telefon */}
                        <td className="px-5 py-4 whitespace-nowrap text-slate-600 font-medium">
                          {customer.phoneNumber || customer.phonenumber || '—'}
                        </td>

                        {/* Status klijenta */}
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className={`inline-block text-[10px] font-bold px-2.5 py-0.5 rounded-md border ${getStatusBadge(customer.status)}`}>
                            {customer.status || 'Zainteresiran'}
                          </span>
                        </td>

                        {/* Status uplata */}
                        <td className="px-5 py-4 whitespace-nowrap">
                          {isOverdue ? (
                            <span className="inline-flex items-center gap-1.5 bg-rose-50 text-rose-700 border border-rose-200/80 text-[11px] font-bold px-2.5 py-1 rounded-lg">
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                              -{overdue.toLocaleString('hr-HR', { minimumFractionDigits: 2 })} € • Kasni
                            </span>
                          ) : isSettled ? (
                            <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200/80 text-[11px] font-bold px-2.5 py-1 rounded-lg">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                              Uredno
                            </span>
                          ) : (
                            <span className="text-slate-300 font-normal text-xs pl-2">
                              —
                            </span>
                          )}
                        </td>

                        {/* Ukupno uplaćeno */}
                        <td className="px-5 py-4 whitespace-nowrap font-extrabold text-slate-900">
                          {totalPaid > 0 ? (
                            <span>{totalPaid.toLocaleString('hr-HR', { minimumFractionDigits: 2 })} €</span>
                          ) : (
                            <span className="text-slate-400 font-normal">0,00 €</span>
                          )}
                        </td>

                        {/* Strelica */}
                        <td className="px-5 py-4 text-right whitespace-nowrap text-slate-300 group-hover:text-slate-700 transition font-bold text-sm">
                          →
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}